import React, { useEffect, useState } from 'react'
import { NameDto } from '@/api/Common/Types'
import { DataCard } from '@/components/DataCard/DataCard'
import {
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
// import { Heading3 } from '@/components/ui/typography'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import {
  CategoryType,
  isDocumentCategoryPersonal,
  StatusType,
  TagType,
  UpdateClientDocument,
  UpdateClientFullNameAndDateOfBirth,
} from 'api/Documents/Types/ClientDocument'
import { DocumentDto } from 'api/Documents/Types/Document'
import { CountryDto } from 'api/Identity/Types/Country'
import { addYears, subYears } from 'date-fns'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { createDateAsUTC } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'

import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal'

interface EditDocumentFormValues extends UpdateClientFullNameAndDateOfBirth {
  id: string
  name: string
  statusId: number
  categoryId: number
  typeId: number | null
  expirationAt?: string | null
  tagIds: number[]
  personalIdNumber: string
  number: string
  countryId: string | null
  relationDocumentId: string | null
}
const normalizeTagName = (tagName: string) => tagName.trim().toLowerCase()

const requiredFieldsByTag: Record<string, (keyof EditDocumentFormValues)[]> = {
  id: ['number', 'personalIdNumber'],
  dateofbirth: ['dateOfBirth'],
}

const EditDocumentFormUI: React.FC<FormikProps<EditDocumentFormValues> & OuterProps> = (props) => {
  const {
    handleSubmit,
    setSubmitting,
    isSubmitting,
    values,
    setFieldValue,
    setTouched,
    validateForm,
    documents,
    tag,
    dateFormatType,
  } = props
  const [tagInUseModal, setTagInUseModal] = useState(false)

  useEffect(() => {
    if (values.categoryId) {
      props.getDocumentTypeBasedOnCategory(values.categoryId.toString())
    }
  }, [values.categoryId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const errors = await validateForm()

    setTouched({
      ...props.touched,
      ...getTouchedFieldsForSelectedTags(),
    })

    if (Object.keys(errors).length > 0) {
      return
    }

    const idTagId = tag.find((tagItem) => tagItem.name === 'ID')?.id

    const isIdDocumentTagInUse = documents.some((document) =>
      document.tags.some((documentTag) => documentTag.name === 'ID')
    )

    const isAddingIdTag = idTagId != null && values.tagIds.includes(idTagId)

    if (isIdDocumentTagInUse && isAddingIdTag) {
      setTagInUseModal(true)
      return
    }

    handleSubmit()
  }

  const submitValues = () => {
    setSubmitting(true)
    handleSubmit()
  }

  const getTouchedFieldsForSelectedTags = () => {
    const touchedFields: Partial<Record<keyof EditDocumentFormValues, boolean>> = {
      typeId: true,
    }

    const selectedTagNames = tag
      .filter((tagItem) => values.tagIds.includes(tagItem.id))
      .map((tagItem) => tagItem.name)

    selectedTagNames.forEach((tagName) => {
      const normalizedTagName = normalizeTagName(tagName)

      requiredFieldsByTag[normalizedTagName]?.forEach((fieldName) => {
        touchedFields[fieldName] = true
      })
    })

    return touchedFields
  }

  const handleMultiSelectChange = (
    fieldName: 'tagIds',
    value: string[] | number[] | string | number | undefined
  ) => {
    const nextValue = Array.isArray(value)
      ? value.map((item) => Number(item))
      : value == null
        ? []
        : [Number(value)]

    setFieldValue(fieldName, nextValue)
  }

  const resolvedDateFormat = dateFormatType || 'dd/MM/yyyy'

  return (
    <Dialog open={Boolean(props.isOpen)} onOpenChange={(open) => !open && props.cancel()}>
      {tagInUseModal && (
        <ConfirmationModal
          contentText='You have requested to use a document tag that is already in use. Please confirm your action.'
          yesButtonText='Proceed'
          onYes={submitValues}
          onCancel={() => setTagInUseModal(false)}
          visible={tagInUseModal}
        />
      )}

      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Edit Document</DialogTitle>
        </DialogHeader>
        <Form onSubmit={handleSubmitForm}>
          <div className='flex flex-col gap-4'>
            <DataCard title='Client Details' columns={2}>
              <FormField
                name='fullName.firstName'
                label='First Name'
                dataTest='user-document-fullName-firstName-field'
              />
              <FormField
                name='fullName.middleName'
                label='Middle Name'
                dataTest='user-document-fullName-middleName-field'
              />
              <FormField
                name='fullName.lastName'
                label='Last Name'
                dataTest='user-document-fullName-lastName-field'
              />
              <FormDateField
                name='dateOfBirth'
                label='Date of Birth'
                dateFormatType={resolvedDateFormat}
              />
            </DataCard>
            <DataCard title='Document Details' columns={2}>
              <FormReadonlyField
                label='File Name'
                value={values.name}
                dataTest='user-document-filename-field'
              />
              <FormSelectField
                name='categoryId'
                label='Category'
                options={props.categoriesType}
                onChange={(value) => {
                  setFieldValue('categoryId', Number(value))
                  setFieldValue('typeId', null)
                }}
              />
              <FormSelectField
                name='typeId'
                label='Type'
                options={props.documentTypesByCategory[values.categoryId] ?? props.documentType}
                placeholder='Select type'
                error={props.touched.typeId ? props.errors.typeId : undefined}
                onChange={(value) => setFieldValue('typeId', value ? Number(value) : null)}
              />
              <FormSelectField
                name='statusId'
                label='Status'
                options={props.documentStatus}
                onChange={(value) => setFieldValue('statusId', Number(value))}
              />
              <FormDateField
                name='expirationAt'
                label='Expiration'
                dateFormatType={resolvedDateFormat}
                isClearable
                minDate={subYears(new Date(), 5)}
                maxDate={addYears(new Date(), 200)}
                onChange={(value) =>
                  setFieldValue('expirationAt', value ? createDateAsUTC(value).toISOString() : null)
                }
              />
              <FormSelectField
                name='relationDocumentId'
                label='Relation'
                options={props.documents}
                value={values.relationDocumentId ?? undefined}
                placeholder='Related document'
                allowClear
                showSearch
                onChange={(value) =>
                  setFieldValue('relationDocumentId', value ? String(value) : null)
                }
              />
            </DataCard>
            {isDocumentCategoryPersonal(values.categoryId) && (
              <DataCard title='Personal Document Details' columns={2}>
                <FormField
                  name='number'
                  label='Id Number'
                  dataTest='user-document-idNumber-field'
                />
                <FormField
                  name='personalIdNumber'
                  label='Personal Id Number'
                  dataTest='user-document-personalIdNumber-field'
                />
                <FormMultiSelectField
                  name='tagIds'
                  label='Tags'
                  options={props.tag}
                  value={values.tagIds}
                  placeholder='Select tags'
                  allowClear
                  showSearch
                  onChange={(value) => handleMultiSelectChange('tagIds', value)}
                />
                <FormSelectField
                  name='countryId'
                  label='Country'
                  options={props.countries}
                  value={values.countryId ?? undefined}
                  placeholder='Select country'
                  allowClear
                  showSearch
                  onChange={(value) => setFieldValue('countryId', value ? String(value) : null)}
                />
              </DataCard>
            )}
          </div>

          <DialogFooter>
            <Button disabled={isSubmitting} type='button' variant='outline' onClick={props.cancel}>
              Cancel
            </Button>
            <Button disabled={isSubmitting} type='submit'>
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function dataToSubmit(values: EditDocumentFormValues): FormSubmitValues<UpdateClientDocument> {
  const formValues = isDocumentCategoryPersonal(values.categoryId)
    ? {
        personalIdNumber: values.personalIdNumber,
        number: values.number,
        countryId: values.countryId || null,
        tags: values.tagIds,
      }
    : {
        personalIdNumber: null,
        number: null,
        countryId: null,
        tags: [],
      }

  return {
    ...formValues,
    id: values.id,
    statusId: values.statusId.toString(),
    expirationAt: values.expirationAt || null,
    typeId: values.typeId || null,
    relations: values.relationDocumentId
      ? [{ documentId: values.relationDocumentId, typeId: 1 }]
      : [],
    categoryId: values.categoryId.toString(),
    fullName: values.fullName,
    dateOfBirth: values.dateOfBirth || null,
  }
}

interface OuterProps {
  isOpen?: boolean
  document: DocumentDto
  categoriesType: CategoryType[]
  documentStatus: StatusType[]
  documentType: NameDto[]
  documentTypesByCategory: Record<string | number, NameDto[]>
  tag: TagType[]
  client: ClientDetailedDto
  dateFormatType?: string
  countries: CountryDto[]
  documents: { id: string; name: string; tags: TagType[] }[]

  getDocumentTypeBasedOnCategory(categoryId: string): void
  onSubmit(values: FormSubmitValues<UpdateClientDocument>): Promise<void>
  cancel(): void
}

export const EditDocumentForm = withFormik<OuterProps, EditDocumentFormValues>({
  mapPropsToValues: ({ document, client }) => {
    return {
      id: document.id,
      name: document.name,
      categoryId: document.category.id,
      statusId: document.status.id,
      typeId: document.type.id,
      expirationAt: document.expirationAt || null,
      tagIds: (document.tags || []).map((tagItem: TagType) => tagItem.id),
      relationDocumentId: document.relations?.[0]?.document.id || null,
      personalIdNumber: document.data.personalIdNumber,
      number: document.data.number,
      countryId: document.data.country?.id || null,
      fullName: {
        firstName: client.firstname,
        lastName: client.lastname,
        middleName: client.middlename,
      },
      dateOfBirth: document.dateOfBirth || undefined,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<EditDocumentFormValues> = {}
    if (!values.typeId) {
      errors.typeId = 'Required'
    }

    const selectedTagNames = props.tag
      .filter((tagItem) => values.tagIds.includes(tagItem.id))
      .map((tagItem) => tagItem.name)

    selectedTagNames.forEach((tagName) => {
      const normalizedTagName = normalizeTagName(tagName)

      requiredFieldsByTag[normalizedTagName]?.forEach((fieldName) => {
        const value = values[fieldName]

        const isEmpty =
          value == null || value === '' || (typeof value === 'string' && value.trim() === '')

        if (isEmpty) {
          errors[fieldName] = `Required when ${tagName} tag is selected`
        }
      })
    })

    return errors
  },
  enableReinitialize: true,
})(EditDocumentFormUI)
